import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { homeworkClassLinkAPI } from '../services/api';
import { PROFILE_SHAPES, ShapeSVG } from '../utils/profileShapes';

const BRAND = {
    teal: '#75B9BE',
    cream: '#FDF6EC',
    charcoal: '#2D4042',
    coral: '#F28482',
};

const ClassLinkPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await homeworkClassLinkAPI.getPublic(token);
                setProfiles(res.data.profiles || []);
            } catch {
                setError('This link is not valid, or no tutorials have been approved yet.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [token]);

    const handlePick = (profile) => {
        setSelected(profile.key);
        setTimeout(() => navigate(`/share/${profile.tutorialLinkToken}`), 350);
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: BRAND.cream,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            fontFamily: "'Inter', sans-serif",
        }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@700;800&family=Inter:wght@400;500;600&display=swap');
                .shape-btn {
                    transition: transform 0.15s ease, box-shadow 0.15s ease;
                }
                .shape-btn:hover {
                    transform: translateY(-4px) scale(1.06) !important;
                }
                .shape-btn:active {
                    transform: scale(0.97) !important;
                }
            `}</style>

            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                <div style={{
                    display: 'inline-block',
                    background: BRAND.teal,
                    color: 'white',
                    fontFamily: 'Nunito, sans-serif',
                    fontWeight: '800',
                    fontSize: '0.8em',
                    padding: '4px 14px',
                    borderRadius: '999px',
                    letterSpacing: '1px',
                    marginBottom: '14px',
                }}>
                    CLASS TUTORIAL
                </div>
                <h1 style={{
                    fontFamily: 'Nunito, sans-serif',
                    fontWeight: '800',
                    color: BRAND.charcoal,
                    fontSize: '2.2em',
                    margin: '0 0 10px 0',
                    lineHeight: 1.2,
                }}>
                    Pick Your Shape
                </h1>
                <p style={{
                    color: '#5D7375',
                    margin: 0,
                    maxWidth: '380px',
                    lineHeight: 1.6,
                    fontSize: '1em',
                }}>
                    Choose the shape your teacher gave you to start your tutorial.
                </p>
            </div>

            {loading && (
                <p style={{ color: '#9CA3AF', fontStyle: 'italic' }}>Loading...</p>
            )}

            {error && (
                <div style={{
                    background: '#FFF5F5',
                    border: '2px solid #F28482',
                    borderRadius: '12px',
                    padding: '16px 24px',
                    color: BRAND.charcoal,
                    fontWeight: '600',
                    maxWidth: '380px',
                    textAlign: 'center',
                }}>
                    {error}
                </div>
            )}

            {!loading && !error && profiles.length === 0 && (
                <p style={{ color: '#9CA3AF', fontStyle: 'italic', textAlign: 'center' }}>
                    No tutorials are ready yet — check back soon!
                </p>
            )}

            {!loading && !error && profiles.length > 0 && (
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '20px',
                    justifyContent: 'center',
                    maxWidth: '640px',
                }}>
                    {profiles.map((profile) => {
                        const shapeData = PROFILE_SHAPES[profile.shapeIndex % PROFILE_SHAPES.length];
                        const isSelected = selected === profile.key;
                        return (
                            <button
                                key={profile.key}
                                className="shape-btn"
                                onClick={() => handlePick(profile)}
                                style={{
                                    background: isSelected ? shapeData.color : shapeData.bg,
                                    border: `3px solid ${shapeData.color}`,
                                    borderRadius: '20px',
                                    padding: '28px 24px',
                                    cursor: 'pointer',
                                    width: '150px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '0',
                                    boxShadow: isSelected
                                        ? `0 0 0 4px ${shapeData.color}55`
                                        : `4px 4px 0px ${shapeData.color}55`,
                                    fontFamily: 'inherit',
                                    outline: 'none',
                                    transform: isSelected ? 'scale(0.97)' : undefined,
                                }}
                            >
                                <ShapeSVG name={shapeData.name} color={isSelected ? 'white' : shapeData.color} size={80} />
                            </button>
                        );
                    })}
                </div>
            )}

            <p style={{ marginTop: '48px', fontSize: '0.78em', color: '#B0BEC5' }}>
                Powered by ClassCompass
            </p>
        </div>
    );
};

export default ClassLinkPage;
